import { useState } from "react"
import { ScheduledPost, PostType, ScheduledPostAudio } from "../types/scheduled-post"
import { MediaItem } from "./useFileUpload"
import { useSelectedAccount } from "../contexts/AccountContext"

const formatForDatetimeLocal = (iso: string): string => {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

type Args = {
  selectedAccountId: number
  isConnected: boolean
  postType: PostType
  items: MediaItem[]
  removedIds: number[]
  isValidatingAny: boolean
  hasFileErrors: boolean
  isUploadingAny: boolean
  hasUploadErrors: boolean
  carouselSizeError: string | null
  onSuccess: (post: ScheduledPost) => void
  editPost?: ScheduledPost
}

export const usePostForm = ({
  selectedAccountId,
  isConnected,
  postType,
  items,
  removedIds,
  isValidatingAny,
  hasFileErrors,
  isUploadingAny,
  hasUploadErrors,
  carouselSizeError,
  onSuccess,
  editPost,
}: Args) => {
  // organization_id はサーバー側の set_organization が未指定時に current_user.organizations.first
  // （id 昇順で決まる、選択中の組織とは限らない）へフォールバックするため必ず送る。CategorySelector /
  // AudioSection と同じく、選択中アカウントが実際に属する組織を逆引きする（決定: #394 関連）。
  const { organizations } = useSelectedAccount()
  const orgId = organizations.find(o => o.accounts.some(a => a.id === selectedAccountId))?.id
  const withOrgParam = (url: string) => orgId != null ? `${url}?organization_id=${orgId}` : url

  const [title, setTitle]             = useState(() => editPost?.title ?? "")
  const [caption, setCaption]         = useState(() => editPost?.caption ?? "")
  const [scheduledAt, setScheduledAt] = useState(() =>
    editPost?.scheduled_at ? formatForDatetimeLocal(editPost.scheduled_at) : ""
  )
  const [scheduledAtError, setScheduledAtError] = useState<string | null>(null)
  const [categoryId, setCategoryId]   = useState<number | null>(() => editPost?.category_id ?? null)
  // リール音源。API の `audio` サブオブジェクトと同じ形にしてあるため editPost.audio を直代入できる。
  // post_type をリール以外に変えても state は保持し、リールに戻したら復活させる（決定 #16）。
  const [audio, setAudioState]        = useState<ScheduledPostAudio | null>(() => editPost?.audio ?? null)
  // 音源に触っていない編集では audio_attributes / remove_audio のどちらも送らない
  // （サーバーは「現状維持」と解釈する）。そのため「触ったか」を state で持つ。
  const [audioTouched, setAudioTouched] = useState(false)
  const [submitting, setSubmitting]       = useState(false)
  const [submitError, setSubmitError]     = useState<string | null>(null)
  const [draftSubmitting, setDraftSubmitting]           = useState(false)
  const [draftError, setDraftError]                     = useState<string | null>(null)
  const [publishNowSubmitting, setPublishNowSubmitting] = useState(false)
  const [publishNowError, setPublishNowError]           = useState<string | null>(null)

  const setAudio = (next: ScheduledPostAudio | null) => {
    setAudioState(next)
    setAudioTouched(true)
  }

  const handleScheduledAtChange = (value: string) => {
    setScheduledAt(value)
    if (!value) { setScheduledAtError(null); return }
    setScheduledAtError(
      new Date(value) <= new Date() ? "予約日時は現在より未来の日時を指定してください" : null
    )
  }

  const tooManyFiles = postType !== "carousel" && items.length > 1

  const canSubmit = (): boolean => {
    if (!isConnected || submitting) return false
    if (!scheduledAt || scheduledAtError) return false
    if (isValidatingAny || hasFileErrors || tooManyFiles || !!carouselSizeError) return false
    if (isUploadingAny || hasUploadErrors) return false
    if (items.length === 0) return false
    if (postType === "carousel" && (items.length < 2 || items.length > 10)) return false
    return true
  }

  const canSubmitDraft = (): boolean => {
    if (submitting || draftSubmitting || publishNowSubmitting) return false
    if (isValidatingAny || hasFileErrors || tooManyFiles || !!carouselSizeError) return false
    if (isUploadingAny || hasUploadErrors) return false
    return true
  }

  const canSubmitPublishNow = (): boolean => {
    if (!isConnected || submitting || draftSubmitting || publishNowSubmitting) return false
    if (isValidatingAny || hasFileErrors || tooManyFiles || !!carouselSizeError) return false
    if (isUploadingAny || hasUploadErrors) return false
    if (items.length === 0) return false
    if (postType === "carousel" && (items.length < 2 || items.length > 10)) return false
    return true
  }

  // リール音源を FormData に積む。
  //
  // - reel 以外の post_type では audio_attributes も remove_audio も送らない（決定 #16。
  //   React state 側は保持し、リールに戻せば復活する）
  // - 音源に触っていない編集ではどちらも送らない（サーバーは現状維持と解釈する）
  // - 削除操作は remove_audio=1（audio_id="" 方式ではない）
  const appendAudio = (formData: FormData) => {
    if (postType !== "reel" || !audioTouched) return

    if (!audio) {
      formData.append("scheduled_post[remove_audio]", "1")
      return
    }

    const prefix = "scheduled_post[audio_attributes]"
    formData.append(`${prefix}[audio_id]`, audio.audio_id)
    if (audio.title != null)          formData.append(`${prefix}[title]`, audio.title)
    if (audio.artist != null)         formData.append(`${prefix}[artist]`, audio.artist)
    if (audio.audio_type != null)     formData.append(`${prefix}[audio_type]`, audio.audio_type)
    if (audio.duration_in_ms != null) formData.append(`${prefix}[duration_in_ms]`, String(audio.duration_in_ms))
    formData.append(`${prefix}[audio_volume]`, String(audio.audio_volume))
    formData.append(`${prefix}[video_volume]`, String(audio.video_volume))
  }

  const buildFormData = (status: string, extraFields?: Record<string, string>) => {
    const formData = new FormData()
    formData.append("scheduled_post[instagram_account_id]", String(selectedAccountId))
    formData.append("scheduled_post[post_type]",            postType)
    formData.append("scheduled_post[title]",                title)
    if (postType !== "story") formData.append("scheduled_post[caption]", caption)
    formData.append("scheduled_post[status]",               status)
    if (categoryId) formData.append("scheduled_post[category_id]", String(categoryId))
    if (extraFields) {
      Object.entries(extraFields).forEach(([k, v]) => formData.append(k, v))
    }

    // Direct Upload済みのsigned_idを送信
    items.forEach(item => {
      if (item.kind === "new" && item.signedId) {
        formData.append("scheduled_post[media_files][]", item.signedId)
      }
    })

    // 最終的な順番を既存IDまたは "new-{i}" で表現
    let newIdx = 0
    items.forEach(item => {
      const slot = item.kind === "existing" ? String(item.id) : `new-${newIdx++}`
      formData.append("scheduled_post[media_order][]", slot)
    })

    // 削除した既存メディアのID
    removedIds.forEach(id => formData.append("scheduled_post[remove_attachment_ids][]", String(id)))

    appendAudio(formData)

    return formData
  }

  const handleDraftSubmit = async () => {
    if (!canSubmitDraft()) return
    setDraftSubmitting(true)
    setDraftError(null)
    try {
      const formData = buildFormData("draft")
      const url    = withOrgParam(editPost ? `/scheduled_posts/${editPost.id}` : "/scheduled_posts")
      const method = editPost ? "PATCH" : "POST"
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      const res = await fetch(url, { method, headers: { Accept: "application/json", "X-CSRF-Token": csrfToken }, body: formData })
      const data = await res.json()
      if (!res.ok) { setDraftError(data.errors?.join(", ") || data.error || "保存に失敗しました"); return }
      onSuccess(data as ScheduledPost)
    } catch {
      setDraftError("ネットワークエラーが発生しました")
    } finally {
      setDraftSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const formData = buildFormData("scheduled", {
        "scheduled_post[scheduled_at]": new Date(scheduledAt).toISOString(),
      })
      const url    = withOrgParam(editPost ? `/scheduled_posts/${editPost.id}` : "/scheduled_posts")
      const method = editPost ? "PATCH" : "POST"
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      const res = await fetch(url, { method, headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken }, body: formData })
      const data = await res.json()
      if (!res.ok) { setSubmitError(data.errors?.join(", ") || data.error || "投稿の保存に失敗しました"); return }
      onSuccess(data as ScheduledPost)
    } catch {
      setSubmitError("ネットワークエラーが発生しました")
    } finally {
      setSubmitting(false)
    }
  }

  const handlePublishNow = async () => {
    if (!canSubmitPublishNow()) return
    setPublishNowSubmitting(true)
    setPublishNowError(null)
    try {
      const formData = buildFormData("publishing")
      const url    = withOrgParam(editPost ? `/scheduled_posts/${editPost.id}` : "/scheduled_posts")
      const method = editPost ? "PATCH" : "POST"
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      const res = await fetch(url, { method, headers: { Accept: "application/json", "X-CSRF-Token": csrfToken }, body: formData })
      const data = await res.json()
      if (!res.ok) { setPublishNowError(data.errors?.join(", ") || data.error || "投稿に失敗しました"); return }
      onSuccess(data as ScheduledPost)
    } catch {
      setPublishNowError("ネットワークエラーが発生しました")
    } finally {
      setPublishNowSubmitting(false)
    }
  }

  const captionLength = caption.length
  const captionCounterClass =
    captionLength > 2000 ? "char-counter--error" :
    captionLength > 1800 ? "char-counter--warn" : ""

  return {
    title, setTitle,
    caption, setCaption, captionLength, captionCounterClass,
    scheduledAt, handleScheduledAtChange, scheduledAtError,
    categoryId, setCategoryId,
    audio, setAudio,
    submitting, submitError,
    draftSubmitting, draftError,
    publishNowSubmitting, publishNowError,
    tooManyFiles, canSubmit, handleSubmit,
    canSubmitDraft, handleDraftSubmit,
    canSubmitPublishNow, handlePublishNow,
  }
}
